import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

_pushScopeId("data-v-e4ac1f74")
const _hoisted_1 = { class: "counter-name" }
const _hoisted_2 = { class: "identifier" }
const _hoisted_3 = {
  key: 0,
  class: "text-muted"
}
const _hoisted_4 = {
  key: 0,
  class: "text-muted"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = {
  key: 1,
  class: "text-muted"
}
const _hoisted_7 = { class: "action-buttons" }
const _hoisted_8 = { class: "section-title" }
const _hoisted_9 = { class: "identifier" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = {
  key: 1,
  class: "text-muted"
}
const _hoisted_12 = {
  key: 0,
  class: "identifier"
}
const _hoisted_13 = {
  key: 1,
  class: "text-muted"
}
const _hoisted_14 = { key: 0 }
const _hoisted_15 = {
  key: 1,
  class: "text-muted"
}
_popScopeId()

import { computed, inject, onMounted, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import {
  callDeleteEntitlementTopOff,
  callFetchEntitlementTopOffs
} from "@/api/licensing";
import DangerConfirmModal from "@/components/modal/DangerConfirmModal.vue";
import EditCounterAllowanceModal from "@/components/modal/EditCounterAllowanceModal.vue";
import TopOffCounterModal from "@/components/modal/TopOffCounterModal.vue";
import { printDate } from "@/utils";
import type { Toasts, UsageCounter, UsageTopOff } from "@/types";


export default /*#__PURE__*/_defineComponent({
  props: {
  entitlementId: {
    type: String,
    required: true
  },
  canEdit: {
    type: Boolean,
    default: false
  }
},
  emits: ["changed"],
  setup(__props, { emit }) {

const props = __props




const { t } = useI18n();
const store = useStore();
const toasts = inject<Toasts | undefined>("toasts");
const handleError = inject<((error: unknown) => void) | undefined>("handleError");

const state = reactive({
  topOffs: [] as UsageTopOff[],
  loadingTopOffs: false,
  showTopOffModal: false,
  topOffCounter: null as UsageCounter | null,
  showEditModal: false,
  editCounter: null as UsageCounter | null,
  showDeleteModal: false,
  deletingTopOff: null as UsageTopOff | null
});

/**
 * Every usage counter of the entitlement, default counter first.
 * Falls back to the legacy singular `usage` when `usages[]` is absent.
 */
const counters = computed<UsageCounter[]>(() => {
  const entitlement = store.state.licensing.entitlementEdit ?? {};
  const list: UsageCounter[] = entitlement.usages?.length
    ? entitlement.usages
    : entitlement.usage
    ? [entitlement.usage]
    : [];
  return list.slice().sort(
    (a, b) => Number(b.isDefault ?? false) - Number(a.isDefault ?? false)
  );
});

const counterColumns = computed(() => {
  const base = ["counter", "type", "included", "topOffs", "total", "assigned", "resetsAt"];
  return props.canEdit ? base.concat("actions") : base;
});

const topOffColumns = computed(() => {
  const base = [
    "counter",
    "allowance",
    "remainingAllowance",
    "createdAt",
    "expiresAt",
    "orderId",
    "notes"
  ];
  return props.canEdit ? base.concat("actions") : base;
});

const isRecurring = (counter: UsageCounter) => counter.type === "RecurringCredits";
const isUnlimited = (counter: UsageCounter) => counter.type === "Unlimited";
const isEditable = (counter: UsageCounter) => !isUnlimited(counter);

const showTopOffs = computed(
  () => state.topOffs.length > 0 || counters.value.some(isRecurring)
);

const counterLabel = (counter: UsageCounter) =>
  counter.name ?? t("entitlements.counters.default");

// The API reports a top-off granted without naming a counter with `counter` null or "default".
const topOffCounterLabel = (topOff: UsageTopOff) =>
  !topOff.counter || topOff.counter === "default"
    ? counterLabel(counters.value.find(c => c.isDefault) ?? { name: null })
    : topOff.counter;

const formatAmount = (value: number | null | undefined) =>
  value === null || value === undefined ? "-" : Number(value).toLocaleString();

const remainingOf = (
  remaining: number | null | undefined,
  total: number | null | undefined,
  counter: UsageCounter
) => {
  if (isUnlimited(counter)) {
    return t("entitlements.counters.unlimited");
  }
  if ((total ?? 0) === 0 && (remaining ?? 0) === 0) {
    return "-";
  }
  return t("entitlements.counters.remainingOf", {
    remaining: formatAmount(remaining ?? 0),
    total: formatAmount(total ?? 0)
  });
};

const loadTopOffs = async () => {
  state.loadingTopOffs = true;
  try {
    const response = await callFetchEntitlementTopOffs(props.entitlementId);
    state.topOffs = response.data ?? [];
  } catch (error: any) {
    state.topOffs = [];
    handleError?.(error?.response?.data ?? error);
    toasts?.error(t("entitlements.counters.loadTopOffsError"));
  } finally {
    state.loadingTopOffs = false;
  }
};

const reloadEntitlement = async () => {
  await store.dispatch("licensing/setEntitlement", props.entitlementId);
};

const refresh = async () => {
  await Promise.all([reloadEntitlement(), loadTopOffs()]);
  emit("changed");
};

const openTopOffModal = (counter: UsageCounter) => {
  state.topOffCounter = counter;
  state.showTopOffModal = true;
};

const closeTopOffModal = () => {
  state.showTopOffModal = false;
  state.topOffCounter = null;
};

const handleGranted = async () => {
  closeTopOffModal();
  await refresh();
};

const openEditModal = (counter: UsageCounter) => {
  state.editCounter = counter;
  state.showEditModal = true;
};

const closeEditModal = () => {
  state.showEditModal = false;
  state.editCounter = null;
};

const handleUpdated = async () => {
  closeEditModal();
  // The PATCH response already refreshed the store; only the top-offs need reloading.
  await loadTopOffs();
  emit("changed");
};

const openDeleteModal = (topOff: UsageTopOff) => {
  state.deletingTopOff = topOff;
  state.showDeleteModal = true;
};

const closeDeleteModal = () => {
  state.showDeleteModal = false;
  state.deletingTopOff = null;
};

const confirmDelete = async () => {
  const topOff = state.deletingTopOff;
  closeDeleteModal();
  if (!topOff) {
    return;
  }
  try {
    await callDeleteEntitlementTopOff(props.entitlementId, topOff.id);
    toasts?.info(t("entitlements.counters.deleteSuccess"));
    await refresh();
  } catch (error: any) {
    handleError?.(error?.response?.data ?? error);
    toasts?.error(t("entitlements.counters.deleteError"));
  }
};

watch(
  () => props.entitlementId,
  () => {
    loadTopOffs();
  }
);

onMounted(loadTopOffs);

return (_ctx: any,_cache: any) => {
  const _component_va_button = _resolveComponent("va-button")!
  const _component_va_card_title = _resolveComponent("va-card-title")!
  const _component_va_badge = _resolveComponent("va-badge")!
  const _component_va_data_table = _resolveComponent("va-data-table")!
  const _component_va_card_content = _resolveComponent("va-card-content")!
  const _component_va_card = _resolveComponent("va-card")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_va_card, null, {
      default: _withCtx(() => [
        _createVNode(_component_va_card_title, { class: "tile-header" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.title")) + " ", 1),
            _createVNode(_component_va_button, {
              class: "ml-2",
              flat: "",
              size: "small",
              icon: "refresh",
              loading: _unref(state).loadingTopOffs,
              onClick: refresh
            }, null, 8, ["loading"])
          ]),
          _: 1
        }),
        _createVNode(_component_va_card_content, null, {
          default: _withCtx(() => [
            _createVNode(_component_va_data_table, {
              items: _unref(counters),
              columns: _unref(counterColumns),
              "no-data-html": _ctx.$t('entitlements.counters.noCounters')
            }, {
              "header(counter)": _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.counter")), 1)
              ]),
              "cell(counter)": _withCtx(({ rowIndex }) => [
                _createElementVNode("span", _hoisted_1, [
                  _createElementVNode("span", _hoisted_2, _toDisplayString(counterLabel(_unref(counters)[rowIndex])), 1),
                  (_unref(counters)[rowIndex].isDefault)
                    ? (_openBlock(), _createBlock(_component_va_badge, {
                        key: 0,
                        text: _ctx.$t('entitlements.counters.default'),
                        color: "#B3B3A8"
                      }, null, 8, ["text"]))
                    : _createCommentVNode("", true)
                ]),
                (_unref(counters)[rowIndex].description)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(counters)[rowIndex].description), 1))
                  : _createCommentVNode("", true)
              ]),
              "header(type)": _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.type")), 1)
              ]),
              "cell(type)": _withCtx(({ rowIndex }) => [
                _createTextVNode(_toDisplayString(_unref(counters)[rowIndex].type ?? "-") + " ", 1),
                (_unref(counters)[rowIndex].unit)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_4, " (" + _toDisplayString(_unref(counters)[rowIndex].unit) + ") ", 1))
                  : _createCommentVNode("", true)
              ]),
              "header(included)": _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.included")), 1)
              ]),
              "cell(included)": _withCtx(({ rowIndex }) => [
                _createTextVNode(_toDisplayString(remainingOf(_unref(counters)[rowIndex].remainingAllowance, _unref(counters)[rowIndex].allowance, _unref(counters)[rowIndex])), 1)
              ]),
              "header(topOffs)": _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.topOffs")), 1)
              ]),
              "cell(topOffs)": _withCtx(({ rowIndex }) => [
                _createTextVNode(_toDisplayString(remainingOf(_unref(counters)[rowIndex].totalTopOffRemainingAllowance, _unref(counters)[rowIndex].totalTopOffAllowance, _unref(counters)[rowIndex])), 1)
              ]),
              "header(total)": _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.total")), 1)
              ]),
              "cell(total)": _withCtx(({ rowIndex }) => [
                _createElementVNode("strong", null, _toDisplayString(remainingOf(_unref(counters)[rowIndex].totalRemainingAllowance, _unref(counters)[rowIndex].totalAllowance, _unref(counters)[rowIndex])), 1)
              ]),
              "header(assigned)": _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.assigned")), 1)
              ]),
              "cell(assigned)": _withCtx(({ rowIndex }) => [
                _createTextVNode(_toDisplayString(formatAmount(_unref(counters)[rowIndex].assignedAllowance)), 1)
              ]),
              "header(resetsAt)": _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.resetsAt")), 1)
              ]),
              "cell(resetsAt)": _withCtx(({ rowIndex }) => [
                (_unref(counters)[rowIndex].resetsAt)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_unref(printDate)(_unref(counters)[rowIndex].resetsAt)), 1))
                  : (_openBlock(), _createElementBlock("span", _hoisted_6, "-"))
              ]),
              "header(actions)": _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.actions")), 1)
              ]),
              "cell(actions)": _withCtx(({ rowIndex }) => [
                _createElementVNode("div", _hoisted_7, [
                  (isRecurring(_unref(counters)[rowIndex]))
                    ? (_openBlock(), _createBlock(_component_va_button, {
                        key: 0,
                        color: "primary",
                        size: "small",
                        icon: "add_circle",
                        onClick: ($event: any) => (openTopOffModal(_unref(counters)[rowIndex]))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.topOff")), 1)
                        ]),
                        _: 2
                      }, 1032, ["onClick"]))
                    : (isEditable(_unref(counters)[rowIndex]))
                      ? (_openBlock(), _createBlock(_component_va_button, {
                          key: 1,
                          color: "primary",
                          size: "small",
                          flat: "",
                          icon: "edit",
                          onClick: ($event: any) => (openEditModal(_unref(counters)[rowIndex]))
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.editAllowance")), 1)
                          ]),
                          _: 2
                        }, 1032, ["onClick"]))
                      : _createCommentVNode("", true)
                ])
              ]),
              _: 1
            }, 8, ["items", "columns", "no-data-html"])
          ]),
          _: 1
        }),
        (_unref(showTopOffs))
          ? (_openBlock(), _createBlock(_component_va_card_content, { key: 0 }, {
              default: _withCtx(() => [
                _createElementVNode("h3", _hoisted_8, _toDisplayString(_ctx.$t("entitlements.counters.topOffsTitle")), 1),
                _createVNode(_component_va_data_table, {
                  items: _unref(state).topOffs,
                  columns: _unref(topOffColumns),
                  "no-data-html": _ctx.$t('entitlements.counters.noTopOffs')
                }, {
                  "header(counter)": _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.counter")), 1)
                  ]),
                  "cell(counter)": _withCtx(({ rowIndex }) => [
                    _createElementVNode("span", _hoisted_9, _toDisplayString(topOffCounterLabel(_unref(state).topOffs[rowIndex])), 1)
                  ]),
                  "header(allowance)": _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.allowance")), 1)
                  ]),
                  "cell(allowance)": _withCtx(({ rowIndex }) => [
                    _createTextVNode(_toDisplayString(formatAmount(_unref(state).topOffs[rowIndex].allowance)), 1)
                  ]),
                  "header(remainingAllowance)": _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.remaining")), 1)
                  ]),
                  "cell(remainingAllowance)": _withCtx(({ rowIndex }) => [
                    _createTextVNode(_toDisplayString(formatAmount(_unref(state).topOffs[rowIndex].remainingAllowance)), 1)
                  ]),
                  "header(createdAt)": _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.createdAt")), 1)
                  ]),
                  "cell(createdAt)": _withCtx(({ rowIndex }) => [
                    _createTextVNode(_toDisplayString(_unref(printDate)(_unref(state).topOffs[rowIndex].createdAt)), 1)
                  ]),
                  "header(expiresAt)": _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.expiresAt")), 1)
                  ]),
                  "cell(expiresAt)": _withCtx(({ rowIndex }) => [
                    (_unref(state).topOffs[rowIndex].expiresAt)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(_unref(printDate)(_unref(state).topOffs[rowIndex].expiresAt)), 1))
                      : (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(_ctx.$t("entitlements.counters.neverExpires")), 1))
                  ]),
                  "header(orderId)": _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.orderId")), 1)
                  ]),
                  "cell(orderId)": _withCtx(({ rowIndex }) => [
                    (_unref(state).topOffs[rowIndex].orderId)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(_unref(state).topOffs[rowIndex].orderId), 1))
                      : (_openBlock(), _createElementBlock("span", _hoisted_13, "-"))
                  ]),
                  "header(notes)": _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.notes")), 1)
                  ]),
                  "cell(notes)": _withCtx(({ rowIndex }) => [
                    (_unref(state).topOffs[rowIndex].notes)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(_unref(state).topOffs[rowIndex].notes), 1))
                      : (_openBlock(), _createElementBlock("span", _hoisted_15, "-"))
                  ]),
                  "header(actions)": _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.actions")), 1)
                  ]),
                  "cell(actions)": _withCtx(({ rowIndex }) => [
                    (props.canEdit)
                      ? (_openBlock(), _createBlock(_component_va_button, {
                          key: 0,
                          color: "#DC2626",
                          flat: "",
                          icon: "delete",
                          onClick: ($event: any) => (openDeleteModal(_unref(state).topOffs[rowIndex]))
                        }, null, 8, ["onClick"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["items", "columns", "no-data-html"])
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    (_unref(state).topOffCounter)
      ? (_openBlock(), _createBlock(TopOffCounterModal, {
          key: 0,
          "is-open": _unref(state).showTopOffModal,
          "entitlement-id": props.entitlementId,
          counter: _unref(state).topOffCounter,
          onCancel: closeTopOffModal,
          onGranted: handleGranted
        }, null, 8, ["is-open", "entitlement-id", "counter"]))
      : _createCommentVNode("", true),
    (_unref(state).editCounter)
      ? (_openBlock(), _createBlock(EditCounterAllowanceModal, {
          key: 1,
          "is-open": _unref(state).showEditModal,
          "entitlement-id": props.entitlementId,
          counter: _unref(state).editCounter,
          onCancel: closeEditModal,
          onUpdated: handleUpdated
        }, null, 8, ["is-open", "entitlement-id", "counter"]))
      : _createCommentVNode("", true),
    _createVNode(DangerConfirmModal, {
      "is-open": _unref(state).showDeleteModal,
      title: _ctx.$t('entitlements.counters.deleteTopOff'),
      message: _ctx.$t('entitlements.counters.deleteConfirm'),
      "button-validate-message": _ctx.$t('buttons.delete'),
      onCancel: closeDeleteModal,
      onValidate: confirmDelete
    }, null, 8, ["is-open", "title", "message", "button-validate-message"])
  ], 64))
}
}

})